import { useState } from 'react';
import { Link } from 'react-router';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  get,
  isArray,
  kebabCase,
} from 'lodash';
import {
  Tag,
  VibeButton,
  VibeIcon,
  VibeTooltip,
  viMoreVertical,
  viChevronRight,
  color,
} from 'vibeguide';
import './SubNavigation.scss';

function SubNavigation({
  user,
  buttons = [],
  className = '',
  title = '',
  breadcrumbs,
  statusTag = {
    label: '',
    tooltip: '',
    icon: null,
    style: {},
  },
  style = {},
  menuName = '',
  isDropMenu = false,
  disabled = false,
  testIds = {
    root: undefined,
    buttons: undefined,
  },
}) {
  /**
   * Resolve the `data-testid` to use for a sub-navigation button.
   *
   * Priority:
   *   1. Explicit override on the button via `button.testId`
   *   2. Lookup by button text in `testIds.buttons`
   *   3. Auto-derived `subnav-<kebab-text>-button` for stable defaults
   */
  const resolveButtonTestId = (button) => {
    if (button.testId) {
      return button.testId;
    }

    if (testIds && testIds.buttons && testIds.buttons[button.text]) {
      return testIds.buttons[button.text];
    }

    if (button.text) {
      return `subnav-${kebabCase(button.text)}-button`;
    }

    return undefined;
  };
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const getButtonsToShow = () => {
    return buttons.map((button) => {
      if (button.show === false) {
        // logic handled by the component passing the buttons, do not show here
        return null;
      }

      let show = true;

      if (button.userCan) {
        if (!isArray(button.userCan)) {
          // convert the permission check to an array
          button.userCan = [button.userCan];
        }

        // check permissions to see if the button should be shown
        button.userCan.forEach((permission) => {
          if (!user.can(permission)) {
            show = false;
          }
        });
      }

      // check if the user has any of the provided permissions
      if (button.userAnyOf && user.hasAnyOf(button.userAnyOf)) {
        return button;
      }

      // user does not have permission (from check above)
      if (button.userAnyOf || !show) {
        // do not show the button
        return null;
      }

      return button;
    }).filter(button => button !== null);
  };

  const onMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const onMenuClose = () => {
    setAnchorEl(null);
  };

  const menuItems = getButtonsToShow();

  return (
    <div
      className={classNames('SubNavigation', className)}
      style={style}
      data-testid={testIds?.root}
    >
      <div className="header">
        {(breadcrumbs && breadcrumbs.length) ? (
          <div className="title breadcrumb">
            {breadcrumbs.map((bc, idx) => {
              const title = get(bc, 'title');
              // extra items to show under the breadcrumb title
              const extraLinks = get(bc, 'links', []);
              // last breadcrumb item
              const last = idx === (breadcrumbs.length - 1);

              return (
                <div key={idx} className="breadcrumb-item">
                  <div className="breadcrumb-title">
                    <div className="breadcrumb-title-inner">
                      {bc.icon && (
                        <VibeIcon
                          icon={bc.icon}
                          color={bc.iconColor ?? color.primary}
                          size={24}
                          tooltip={bc.iconTooltip ?? ''}
                        />
                      )}

                      <div className="link-container">
                        <Link
                          className={classNames({ last })}
                          to={bc.linkUrl}
                        >
                          {title}
                        </Link>
                      </div>

                      {bc.tags && (
                        <div className="tags">
                          {bc.tags}
                        </div>
                      )}
                    </div>

                    {extraLinks.length > 0 && (
                      <div className="extra-details">
                        {extraLinks.map((link) => (
                          <div
                            key={`extra-${link.label}`}
                            className="extra-item"
                          >
                            <div className="label">
                              {link.label}
                            </div>

                            {link.url && (
                              <Link to={link.url}>
                                {link.value}
                              </Link>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {!last && (
                    <div className="arrow-container">
                      <VibeIcon
                        style={{
                          margin: '0 4px',
                        }}
                        icon={viChevronRight}
                        color={color.manatee}
                        size={32}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <h1 className="title">
            {title}
          </h1>
        )}

        {(menuItems.length > 0 && !isDropMenu) && (
          <div className="sub-navigation-buttons">
            {menuItems.map((button) => (
              <div key={`sub-nav-btn-${button.text}`}>
                <VibeButton
                  className="sub-nav-btn"
                  text={button.text}
                  color={button.color ?? 'secondary'}
                  icon={button.icon}
                  tooltip={button.tooltip}
                  disabled={button.disabled}
                  link={button.url}
                  testId={resolveButtonTestId(button)}
                  onClick={button.onClick}
                />
              </div>
            ))}
          </div>
        )}

        {((menuItems.length > 0 && isDropMenu) && !disabled) && (
          <div className="actions-container">
            {statusTag && statusTag.label && (
              <div className="status-container">
                <VibeTooltip
                  title={statusTag.tooltip || ''}
                  placement="left"
                >
                  <div
                    style={{
                      marginLeft: 8,
                    }}
                  >
                    <Tag
                      tag={{
                        name: statusTag.label,
                        icon: statusTag.icon,
                      }}
                      style={{
                        fontSize: 12,
                        textTransform: 'uppercase',
                        ...statusTag.style,
                      }}
                    />
                  </div>
                </VibeTooltip>
              </div>
            )}

            <VibeIcon
              icon={viMoreVertical}
              color={color.manatee}
              hoverColor={color.obsidian}
              size={24}
              onClick={onMenuOpen}
              data-testid="subnav-more-menu-button"
            />
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={onMenuClose}
              className="sub-nav-menu"
            >
              {menuName && (
                <h3 className="sub-menu-header">{menuName}</h3>
              )}

              {menuItems.map((button, index) => {
                const buttonTestId = resolveButtonTestId(button);

                if (button.text === 'Details') {
                  return (
                    <Link to={button.url} key={index}>
                      <MenuItem
                        key={index}
                        onClick={(e) => {
                          button.onClick(e);
                          onMenuClose();
                        }}
                        className="sub-menu-item"
                        disabled={button.disabled}
                        style={{
                          color: button.color || color.obsidian,
                        }}
                        data-testid={buttonTestId}
                      >
                        <ListItemIcon>
                          <VibeIcon
                            icon={button.icon}
                            color={button.color || color.obsidian}
                            size={16}
                          />
                        </ListItemIcon>
                        <ListItemText sx={{ color: button.color }}>{button.text}</ListItemText>
                      </MenuItem>
                    </Link>
                  );
                }

                return (button.disabled && button.tooltip) ? (
                  <VibeTooltip
                    key={`tooltip-${index}`}
                    title={button.tooltip}
                    placement="left"
                  >
                    <div>
                      <MenuItem
                        key={index}
                        onClick={(e) => {
                          button.onClick(e);
                          onMenuClose();
                        }}
                        className="sub-menu-item"
                        disabled={button.disabled}
                        style={{
                          color: button.color || color.obsidian,
                        }}
                        data-testid={buttonTestId}
                      >
                        <ListItemIcon>
                          <VibeIcon
                            icon={button.icon}
                            color={button.color || color.obsidian}
                            size={16}
                          />
                        </ListItemIcon>
                        <ListItemText sx={{ color: button.color }}>{button.text}</ListItemText>
                      </MenuItem>
                    </div>
                  </VibeTooltip>
                ) : (
                  <MenuItem
                    key={index}
                    onClick={(e) => {
                      button.onClick(e);
                      onMenuClose();
                    }}
                    className="sub-menu-item"
                    disabled={button.disabled}
                    style={{
                      color: button.color || color.obsidian,
                    }}
                    data-testid={buttonTestId}
                  >
                    <ListItemIcon>
                      <VibeIcon
                        icon={button.icon}
                        color={button.color || color.obsidian}
                        size={16}
                      />
                    </ListItemIcon>
                    <ListItemText sx={{ color: button.color }}>{button.text}</ListItemText>
                  </MenuItem>
                );
              })}
            </Menu>
          </div>
        )}
      </div>
    </div>
  );
}

SubNavigation.propTypes = {
  className: PropTypes.string,
  /** Custom style */
  style: PropTypes.oneOfType([
    PropTypes.object,
  ]),
  /** Title of the page */
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  /** Buttons */
  buttons: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    icon: PropTypes.func,
    iconProps: PropTypes.shape({
      color: PropTypes.string,
      size: PropTypes.number,
    }),
    url: PropTypes.string,
    userCan: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    userAnyOf: PropTypes.arrayOf(PropTypes.string),
    tooltip: PropTypes.string,
    disabled: PropTypes.bool,
    color: PropTypes.string,
    textColor: PropTypes.string,
    /** data-testid for this specific button (overrides auto-derived value) */
    testId: PropTypes.string,
    onClick: PropTypes.func,
  })),
  statusTag: PropTypes.shape({
    label: PropTypes.string,
    tooltip: PropTypes.string,
    icon: PropTypes.func,
    style: PropTypes.object,
  }),
  menuName: PropTypes.string,
  isDropMenu: PropTypes.bool,
  disabled: PropTypes.bool,
  /**
   * data-testid attributes for E2E test selectors.
   * - root: applied to the SubNavigation root container
   * - buttons: optional map of button.text -> data-testid override; otherwise
   *   buttons fall back to `subnav-<kebab-text>-button`.
   */
  testIds: PropTypes.shape({
    root: PropTypes.string,
    buttons: PropTypes.objectOf(PropTypes.string),
  }),
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

export default connect(mapStateToProps)(SubNavigation);
